/*
 * Vimeo → GA4 tracking frontend
 * Phase 2: Detect Vimeo iframes, attach listeners, and dispatch GA4/GTM events.
 */

import Player from '@vimeo/player';

type GAParam = Record<string, unknown>;

type Config = {
  measurementId?: string;
  thresholds?: number[];
  requireExistingGtag?: boolean;
  sendOnExit?: boolean;
  selector?: string;
  debug?: boolean;
  localLogging: boolean;
  restUrl: string | null;
  restNonce: string | null;
};

type PlayerState = {
  started: boolean;
  completed: boolean;
  firedProgress: Set<number>;
  lastPercent: number;
  lastCurrentTime: number;
  duration: number;
  videoId?: number;
  title?: string;
  url?: string;
};

(function () {
  const defaults: Required<Omit<Config, 'measurementId' | 'selector'>> & Pick<Config, 'measurementId' | 'selector'> = {
    measurementId: '',
    thresholds: [10, 25, 50, 75, 90],
    requireExistingGtag: true,
    sendOnExit: false,
    selector: undefined,
    debug: false,
    localLogging: false,
    restUrl: null,
    restNonce: null,
  };

  const cfg: Config = Object.assign({}, defaults, (window as any).VGA4_CONFIG || {});

  const getCookie = (name: string): string | null => {
    const raw = document.cookie || '';
    if (!raw) return null;
    const parts = raw.split(';');
    for (const part of parts) {
      const [key, ...rest] = part.split('=');
      if (key && key.trim() === name) {
        return decodeURIComponent(rest.join('=').trim());
      }
    }
    return null;
  };

  const viewerId = getCookie('lex_videos_user_id');

  const dlog = (...args: any[]) => {
    if (cfg.debug) {
      // eslint-disable-next-line no-console
      console.debug('[VGA4]', ...args);
    }
  };

  const supportsGtag = () => typeof (window as any).gtag === 'function';
  const supportsDataLayer = () => Array.isArray((window as any).dataLayer);

  const pageContext = () => ({
    page_path: window.location.pathname + window.location.search,
    page_title: document.title,
  });

  const baseParams = (extra: GAParam): GAParam => ({
    ...pageContext(),
    player: 'vimeo',
    ...(viewerId ? { viewer_id: viewerId } : {}),
    ...extra,
  });

  const logToWordPress = (eventName: string, params: GAParam): void => {
    if (!cfg.localLogging || !cfg.restUrl) {
      return;
    }

    const payload = JSON.stringify({ event_name: eventName, params });

    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/json' });
        const ok = navigator.sendBeacon(cfg.restUrl, blob);
        if (ok) return;
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (cfg.restNonce) {
        headers['X-WP-Nonce'] = cfg.restNonce;
      }

      // Use keepalive so the request can complete during page unload.
      void fetch(cfg.restUrl, {
        method: 'POST',
        headers,
        body: payload,
        keepalive: true,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      if (cfg.debug) console.warn('[VGA4] local log error', e);
    }
  };

  function emit(eventName: string, params: GAParam): void {
    logToWordPress(eventName, params);
    try {
      if (supportsGtag()) {
        dlog('emit gtag', eventName, params);
        (window as any).gtag('event', eventName, params);
        return;
      }
      if (supportsDataLayer()) {
        dlog('emit dataLayer', eventName, params);
        (window as any).dataLayer.push({ event: eventName, ...params });
        return;
      }
      if (cfg.requireExistingGtag) {
        dlog('emit skipped (requireExistingGtag=true), missing gtag/dataLayer', eventName, params);
        return;
      }
      dlog('emit (no gtag/dataLayer):', eventName, params);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[VGA4] emit error', e);
    }
  }

  const bound = new WeakSet<HTMLIFrameElement>();

  function shouldBind(iframe: HTMLIFrameElement): boolean {
    if (!iframe || bound.has(iframe)) return false;
    try {
      const src = iframe.getAttribute('src') || '';
      const isVimeo = /(^|\.)vimeo\.com\//.test(src) || iframe.hasAttribute('data-vimeo-id');
      return isVimeo;
    } catch {
      return false;
    }
  }

  async function bindPlayer(iframe: HTMLIFrameElement): Promise<void> {
    if (!shouldBind(iframe)) return;
    bound.add(iframe);

    const player = new Player(iframe);

    const state: PlayerState = {
      started: false,
      completed: false,
      firedProgress: new Set<number>(),
      lastPercent: 0,
      lastCurrentTime: 0,
      duration: 0,
      videoId: undefined,
      title: undefined,
      url: iframe.src || undefined,
    };

    try {
      state.duration = await player.getDuration();
    } catch {/* ignore */}
    try {
      state.title = await player.getVideoTitle();
    } catch {/* ignore */}
    try {
      state.videoId = await player.getVideoId();
    } catch {/* ignore */}

    // Ensure metadata refresh on 'loaded'
    player.on('loaded', async () => {
      try { state.duration = await player.getDuration(); } catch {}
      try { state.title = await player.getVideoTitle(); } catch {}
      try { state.videoId = await player.getVideoId(); } catch {}
    });

    function progressThresholds(): number[] {
      const t = Array.isArray(cfg.thresholds) ? cfg.thresholds.slice() : [10, 25, 50, 75, 90];
      return t
        .map((n) => Math.max(0, Math.min(100, Math.round(n))))
        .filter((v, i, a) => a.indexOf(v) === i) // unique
        .sort((a, b) => a - b);
    }

    const thresholds = progressThresholds();

    // PLAY
    function onStart() {
      if (!state.started) {
        state.started = true;
        emit(
          'video_start',
          baseParams({
            video_id: state.videoId,
            video_title: state.title,
            video_url: state.url,
            duration: state.duration,
          })
        );
      }
    }
    player.on('play', onStart);
    player.on('playing', onStart);

    // PAUSE
    player.on('pause', async () => {
      try {
        const current = await player.getCurrentTime();
        state.lastCurrentTime = current;
        const percent = state.duration ? Math.floor((current / state.duration) * 100) : 0;
        state.lastPercent = percent;
        emit(
          'video_pause',
          baseParams({
            video_id: state.videoId,
            video_title: state.title,
            video_url: state.url,
            duration: state.duration,
            current_time: current,
          })
        );
      } catch {/* ignore */}
    });

    // ENDED
    player.on('ended', () => {
      if (!state.completed) {
        state.completed = true;
        emit(
          'video_complete',
          baseParams({
            video_id: state.videoId,
            video_title: state.title,
            video_url: state.url,
            duration: state.duration,
          })
        );
      }
    });

    // TIMEUPDATE (progress)
    player.on('timeupdate', (data: { seconds: number }) => {
      // Guard: ignore timeupdate until a start event was observed to avoid early firing on load.
      if (!state.started) return;

      const current = data?.seconds || 0;
      state.lastCurrentTime = current;
      const percent = state.duration ? Math.floor((current / state.duration) * 100) : 0;
      state.lastPercent = percent;

      for (const t of thresholds) {
        if (percent >= t && !state.firedProgress.has(t)) {
          state.firedProgress.add(t);
          emit(
            'video_progress',
            baseParams({
              video_id: state.videoId,
              video_title: state.title,
              video_url: state.url,
              duration: state.duration,
              current_time: current,
              percent_progress: t,
            })
          );
        }
      }

      // If progress indicates completion but 'ended' didn't fire (edge case)
      if (!state.completed && percent >= 95) {
        state.completed = true;
        emit(
          'video_complete',
          baseParams({
            video_id: state.videoId,
            video_title: state.title,
            video_url: state.url,
            duration: state.duration,
          })
        );
      }
    });

    // On page hide: optionally send last progress
    if (cfg.sendOnExit) {
      const onPageHide = () => {
        if (!state.started || state.completed) return;
        const params = baseParams({
          video_id: state.videoId,
          video_title: state.title,
          video_url: state.url,
          duration: state.duration,
          current_time: state.lastCurrentTime,
          percent_progress: Math.min(95, state.lastPercent),
          reason: 'pagehide',
        });
        try {
          emit('video_progress', params);
        } catch {/* ignore */}
      };
      window.addEventListener('pagehide', onPageHide);
      window.addEventListener('beforeunload', onPageHide);
    }
  }

  function scan(root: ParentNode = document): void {
    const sel = cfg.selector || 'iframe[src*="vimeo.com"], iframe[data-vimeo-id], iframe[data-vimeo-url]';
    const iframes = Array.from(root.querySelectorAll<HTMLIFrameElement>(sel));
    iframes.forEach((el) => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      bindPlayer(el);
    });
  }

  // Initial scan
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  // Watch for dynamically added iframes
  const mo = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      m.addedNodes.forEach((n) => {
        if (n instanceof HTMLIFrameElement) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          bindPlayer(n);
        } else if (n instanceof Element) {
          scan(n);
        }
      });
    });
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
